// ─── Hook ─────────────────────────────────────────────────────────────────────

import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getApiV1ExamQuestionLibraryQueryKey,
  useDeleteApiV1ExamQuestionLibraryItemId,
} from '@/shared/api/generated';

/**
 * Hook para excluir uma questão da Biblioteca de Questões.
 *
 * Ao excluir com sucesso, invalida o cache da listagem de questões,
 * forçando o re-fetch automático da tabela. Exibe alert de sucesso ou erro.
 *
 * @returns Função `mutate` para acionar a exclusão e estado `isPending`.
 *
 * @example
 * ```tsx
 * const { mutate: deleteQuestion, isPending } = useQuestionDelete();
 * deleteQuestion({ id: 42 });
 * ```
 */
export function useQuestionDelete() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('questionLibrary');
  const { alert } = useAlert();

  return useDeleteApiV1ExamQuestionLibraryItemId({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamQuestionLibraryQueryKey(),
        });
        alert({
          alertType: 'success',
          title: t('alerts.deleteSuccess.title'),
          description: t('alerts.deleteSuccess.description'),
        });
      },
      onError: () => {
        alert({
          alertType: 'danger',
          title: t('alerts.deleteError.title'),
          description: t('alerts.deleteError.description'),
        });
      },
    },
  });
}
